/**
 * Передає service worker'у дані, потрібні для самостійної перепідписки на push.
 *
 * НАВІЩО. Обробник `pushsubscriptionchange` у sw.ts спрацьовує тоді, коли
 * сторінки застосунку може не бути взагалі, і не має доступу до localStorage,
 * де лежить токен. Тому ключ VAPID і поточний токен доступу дублюються
 * у IndexedDB — єдине сховище, спільне для сторінки і SW.
 *
 * Записуємо після входу та після кожного оновлення токена (див.
 * usePushSubscription), очищаємо при виході.
 */

const DB_NAME = 'autostrada-push'
const STORE = 'credentials'
const KEY = 'current'

export interface PushCredentials {
    vapidPublicKey: string
    token: string
}

const openDb = (): Promise<IDBDatabase> =>
    new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, 1)
        request.onupgradeneeded = () => {
            if (!request.result.objectStoreNames.contains(STORE)) {
                request.result.createObjectStore(STORE)
            }
        }
        request.onsuccess = () => resolve(request.result)
        request.onerror = () => reject(request.error)
    })

/**
 * Зберігає облікові дані для перепідписки. Помилки не кидає:
 * недоступний IndexedDB (приватний режим, квота) не має ламати вхід.
 */
export const savePushCredentials = async (credentials: PushCredentials): Promise<void> => {
    try {
        const db = await openDb()
        await new Promise<void>((resolve) => {
            const tx = db.transaction(STORE, 'readwrite')
            tx.objectStore(STORE).put(credentials, KEY)
            tx.oncomplete = () => resolve()
            tx.onerror = () => resolve()
            tx.onabort = () => resolve()
        })
    } catch (e) {
        console.warn('[WebPush] failed to persist push credentials', e)
    }
}

/**
 * Прибирає облікові дані — викликається при виході, щоб SW не міг
 * перепідписати пристрій від імені користувача, який уже вийшов.
 */
export const clearPushCredentials = async (): Promise<void> => {
    try {
        const db = await openDb()
        await new Promise<void>((resolve) => {
            const tx = db.transaction(STORE, 'readwrite')
            tx.objectStore(STORE).delete(KEY)
            tx.oncomplete = () => resolve()
            tx.onerror = () => resolve()
            tx.onabort = () => resolve()
        })
    } catch (e) {
        console.warn('[WebPush] failed to clear push credentials', e)
    }
}
